'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface StartSectionProps {
    isAuthenticated?: boolean;
}

const comparisonData = [
    {
        feature: 'Personalization',
        yourAura: 'Uses your chart, numbers, and optional palm scan',
        generic: 'One-size-fits-all',
    },
    {
        feature: 'Methods Combined',
        yourAura: 'Astrology + Numerology + Palmistry',
        generic: 'Astrology only',
    },
    {
        feature: 'Daily Insights',
        yourAura: 'Daily focus, caution, and reflection',
        generic: 'Vague predictions',
    },
    {
        feature: 'Time Required',
        yourAura: 'Under 2 minutes',
        generic: '5-10 minutes reading',
    },
    {
        feature: 'Transparency',
        yourAura: 'Inputs and reasoning shown',
        generic: 'No transparency',
    },
    {
        feature: 'Palm Reading',
        yourAura: 'AI-assisted analysis included',
        generic: 'Not available',
    },
];

const StartSection = ({ isAuthenticated = false }: StartSectionProps) => {
    return (
        <section id="start" className="relative overflow-hidden bg-[var(--experience-page)] py-20 md:py-24">
            <div className="container mx-auto px-5 relative z-10">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center space-x-2 px-4 py-2 manuscript-shell rounded-full border border-accent/30 mb-4">
                            <Icon name="LockClosedIcon" size={20} className="text-accent" variant="solid" />
                            <span className="text-sm font-medium text-accent">Open Access</span>
                        </div>
                        <h2 className="manuscript-heading text-3xl md:text-5xl font-bold text-foreground mb-4">
                            Create your account and start using YourAura today.
                        </h2>
                        <p className="manuscript-copy text-base md:text-lg max-w-3xl mx-auto">
                            YourAura is open to real users now. Sign up to create your private dashboard, or sign in to continue where you left off.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 mb-12">
                        <div className="card-surface manuscript-frame rounded-2xl p-8 md:p-10">
                            <h3 className="manuscript-subheading text-2xl md:text-3xl font-bold text-foreground mb-6">
                                What opens after signup
                            </h3>

                            <div className="grid gap-4">
                                <BenefitItem
                                    icon="SparklesIcon"
                                    title="Daily brief dashboard"
                                    description="Get a focused daily reading with context from your chart, numerology cycle, and saved profile."
                                />
                                <BenefitItem
                                    icon="HandRaisedIcon"
                                    title="Optional palm context"
                                    description="Add palm scans only when useful. You can still use the core dashboard without uploading a photo."
                                />
                                <BenefitItem
                                    icon="ClockIcon"
                                    title="Saved history"
                                    description="Return to readings, compare patterns, and keep your profile inputs in one private account."
                                />
                            </div>
                        </div>

                        <div className="card-surface manuscript-frame rounded-2xl p-8 md:p-10 border border-accent/20 relative overflow-hidden">
                            <h3 className="manuscript-subheading text-2xl md:text-3xl font-bold text-foreground mb-6 relative z-10">
                                {isAuthenticated ? 'Your dashboard is ready' : 'Start with a real account'}
                            </h3>
                            <p className="text-sm text-muted-foreground mb-6 relative z-10 leading-relaxed">
                                {isAuthenticated
                                    ? 'Open your dashboard for daily guidance, saved readings, palm analysis, and profile tools.'
                                    : 'Create an account, verify your email, and open your private dashboard.'}
                            </p>

                            <div className="relative z-10 space-y-3">
                                <Link
                                    href={isAuthenticated ? '/dashboard' : '/signup'}
                                    className="w-full manuscript-button px-6 py-4 font-semibold rounded-xl shadow-lg transition-smooth flex items-center justify-center space-x-2"
                                >
                                    <span>{isAuthenticated ? 'Open Dashboard' : 'Get My Free Brief'}</span>
                                    <Icon name="ArrowRightIcon" size={20} />
                                </Link>

                                {!isAuthenticated && (
                                    <Link
                                        href="/login"
                                        className="w-full manuscript-panel px-6 py-4 font-semibold rounded-xl border border-[#e8d7b0]/12 text-foreground transition-smooth flex items-center justify-center space-x-2 hover:border-accent/30"
                                    >
                                        <span>Already have an account? Sign in</span>
                                    </Link>
                                )}
                            </div>

                            <div className="mt-6 flex flex-col items-center space-y-2 relative z-10">
                                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                                    <Icon name="LockClosedIcon" size={12} className="text-primary" />
                                    <span>Private profile inputs. Palm scan optional. Direct dashboard access.</span>
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="card-surface manuscript-frame rounded-2xl p-8 md:p-10">
                        <h3 className="manuscript-subheading text-2xl md:text-3xl font-bold text-foreground mb-6 text-center">
                            How YourAura keeps readings usable
                        </h3>

                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-[#e8d7b0]/10">
                                        <th className="text-left py-3 px-4 text-foreground font-semibold">Feature</th>
                                        <th className="text-center py-3 px-4 text-accent font-semibold">YourAura</th>
                                        <th className="text-center py-3 px-4 text-muted-foreground font-semibold">Generic Apps</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {comparisonData.map((row) => (
                                        <tr key={row.feature} className="border-b border-[#e8d7b0]/8">
                                            <td className="py-3 px-4 text-foreground">{row.feature}</td>
                                            <td className="py-3 px-4 text-center">
                                                <div className="flex items-center justify-center space-x-2">
                                                    <Icon name="CheckCircleIcon" size={16} className="text-success" variant="solid" />
                                                    <span className="text-sm text-foreground">{row.yourAura}</span>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <div className="flex items-center justify-center space-x-2">
                                                    <Icon name="XCircleIcon" size={16} className="text-error" variant="solid" />
                                                    <span className="text-sm text-muted-foreground">{row.generic}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const BenefitItem = ({ icon, title, description }: { icon: string; title: string; description: string }) => (
    <div className="flex items-start space-x-3">
        <div className="w-8 h-8 rounded-full bg-accent/15 border border-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Icon name={icon} size={16} className="text-accent" />
        </div>
        <div>
            <p className="font-semibold text-foreground">{title}</p>
            <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
        </div>
    </div>
);

export default StartSection;
